import type { ImagePickerAsset } from "expo-image-picker";

export type ImagePickerRecovery =
  | { kind: "empty" }
  | { kind: "cancelled" }
  | { kind: "error" }
  | { kind: "invalid" }
  | { kind: "success"; assets: ImagePickerAsset[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function classifyImagePickerResult(value: unknown): ImagePickerRecovery {
  if (value === null || value === undefined) return { kind: "empty" };
  if (!isRecord(value)) return { kind: "invalid" };

  if (typeof value.code === "string") return { kind: "error" };
  if (value.canceled === true) return { kind: "cancelled" };
  if (value.canceled !== false || !Array.isArray(value.assets) || value.assets.length === 0) {
    return { kind: "invalid" };
  }

  const assets = value.assets;
  if (assets.some((asset) => !isRecord(asset)
    || typeof asset.uri !== "string"
    || asset.uri.trim().length === 0)) {
    return { kind: "invalid" };
  }

  return { kind: "success", assets: assets as ImagePickerAsset[] };
}

function cameraAssetKey(asset: unknown): string | null {
  if (!isRecord(asset) || typeof asset.uri !== "string") return null;
  const uri = asset.uri.trim();
  return uri.length > 0 ? uri : null;
}

/** Filters duplicate observations of the same camera output within one composer session. */
export function unseenCameraAssets<T>(assets: readonly T[], seenKeys: Set<string>): T[] {
  const unseen: T[] = [];

  for (const asset of assets) {
    const key = cameraAssetKey(asset);
    if (key && seenKeys.has(key)) continue;
    if (key) seenKeys.add(key);
    unseen.push(asset);
  }

  return unseen;
}

/** Appends incoming attachments without replacing already-restored composer content. */
export function appendComposerAttachments<T extends { id: string }>(
  current: readonly T[],
  incoming: readonly T[],
  limit: number
): T[] {
  const result = [...current];
  const ids = new Set(current.map((item) => item.id));

  for (const item of incoming) {
    if (ids.has(item.id)) continue;
    ids.add(item.id);
    result.push(item);
  }

  return result.slice(0, Math.max(0, limit));
}
